import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, renderSlot as _renderSlot, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "site-kit-monaco-editor-root" }
const _hoisted_2 = {
  key: "editor",
  class: "site-kit-monaco-editor-swap-container"
}
const _hoisted_3 = {
  key: "placeholder",
  class: "site-kit-monaco-editor-swap-container"
}
const _hoisted_4 = { class: "site-kit-monaco-editor-placeholder-code" }
const _hoisted_5 = {
  key: "loading",
  class: "site-kit-monaco-editor-placeholder-loading"
}

import type {
  MonacoEditor,
  ProvideCodeActions,
} from "@ota-meshi/site-kit-monaco-editor";
import {
  loadMonacoEditor,
  setupMonacoEditor,
} from "@ota-meshi/site-kit-monaco-editor";
import { onBeforeUnmount, onMounted, ref, watch } from "vue";
export type SourceLocation = {
  start: {
    line: number;
    column: number;
  };
  end: {
    line: number;
    column: number;
  };
};
type MaybePromise<T> = T | Promise<T>;


export default /*@__PURE__*/_defineComponent({
  __name: 'MonacoEditor',
  props: {
    modelValue: { default: "" },
    rightCode: { default: "" },
    language: { default: "javascript" },
    readOnly: { type: Boolean },
    diffEditor: { type: Boolean },
    markers: { default: () => [] },
    rightMarkers: { default: () => [] },
    provideCodeActions: { type: [Function, null], default: null },
    waiting: { default: null }
  },
  emits: ["update:modelValue", "changeCursorPosition", "focusEditorText", "mountedEditor"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;
const emit = __emit;

const rootElement = ref<HTMLDivElement | null>(null);
// let editor: MEditor.IStandaloneDiffEditor | MEditor.IStandaloneCodeEditor | null = null;
// eslint-disable-next-line func-style -- variable
let setModelLanguage: (lang: string) => void = () => {
  // init
};
// eslint-disable-next-line func-style -- variable
let setLeftValue: (value: string) => void = () => {
  // init
};
// eslint-disable-next-line func-style -- variable
let setRightValue: (value: string) => void = () => {
  // init
};
// eslint-disable-next-line func-style -- variable
let setLeftMarkers: (markers: MonacoEditor.IMarkerData[]) => void = () => {
  // init
};
// eslint-disable-next-line func-style -- variable
let setRightMarkers: (markers: MonacoEditor.IMarkerData[]) => void = () => {
  // init
};
// eslint-disable-next-line func-style -- variable
let getLeftEditor: () => MonacoEditor.IStandaloneCodeEditor | null = () => null;
// eslint-disable-next-line func-style -- variable
let getRightEditor: () => MonacoEditor.IStandaloneCodeEditor | null = () =>
  null;
// eslint-disable-next-line func-style -- variable
let disposeEditor: () => void = () => {
  // init
};
// eslint-disable-next-line func-style -- variable
let registerCodeActionProvider: (
  provideCodeActions: ProvideCodeActions,
) => void = () => {
  // init
};

const loading = ref(true);
onMounted(async () => {
  const loadingMonaco = loadMonacoEditor();
  await Promise.all([props.waiting, loadingMonaco]);
  loading.value = false;
});

watch(rootElement, () => {
  void setup();
});
watch(
  () => props.modelValue,
  (value) => {
    setLeftValue(value);
  },
);
watch(
  () => props.rightCode,
  (value) => {
    setRightValue(value);
  },
);
watch(
  () => props.language,
  (value) => {
    setModelLanguage(value);
  },
);
watch(
  () => props.markers,
  (value) => {
    setLeftMarkers(value);
  },
);
watch(
  () => props.rightMarkers,
  (value) => {
    setRightMarkers(value);
  },
);
watch(
  () => props.provideCodeActions,
  (value) => {
    if (value) {
      registerCodeActionProvider(value);
    }
  },
);

watch(
  () => props.diffEditor,
  () => {
    void setup();
  },
);

/** setup monaco */
async function setup() {
  disposeEditor();
  if (loading.value) {
    return;
  }

  // eslint-disable-next-line require-atomic-updates -- no problem
  ({
    getLeftEditor,
    getRightEditor,
    setLeftMarkers,
    setLeftValue,
    setModelLanguage,
    setRightMarkers,
    setRightValue,
    disposeEditor,
    registerCodeActionProvider,
  } = await setupMonacoEditor({
    init: {
      value: props.modelValue,
      markers: props.markers,
      right: {
        value: props.rightCode,
        markers: props.rightMarkers,
      },
      language: props.language,
      readOnly: props.readOnly,
    },
    listeners: {
      onChangeValue: (value) => {
        emit("update:modelValue", value);
      },
      onDidChangeCursorPosition(evt) {
        emit("changeCursorPosition", evt);
      },
      onFocus() {
        emit("focusEditorText");
      },
    },
    rootElement: rootElement.value!,
    useDiffEditor: Boolean(props.diffEditor),
  }));

  if (props.provideCodeActions) {
    registerCodeActionProvider(props.provideCodeActions);
  }

  emit("mountedEditor");
}

onBeforeUnmount(() => {
  disposeEditor();
});

/** Set cursor position */
function setCursorPosition(loc: SourceLocation): void {
  const leftEditor = getLeftEditor();
  if (leftEditor) {
    leftEditor.setSelection({
      startLineNumber: loc.start.line,
      startColumn: loc.start.column,
      endLineNumber: loc.end.line,
      endColumn: loc.end.column,
    });
    leftEditor.revealLineInCenter(loc.start.line);
  }
}

__expose({
  setCursorPosition,
  getLeftEditor() {
    return getLeftEditor();
  },
  getRightEditor() {
    return getRightEditor();
  },
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_Transition, { name: "site-kit-monaco-editor-fade" }, {
      default: _withCtx(() => [
        (!loading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createElementVNode("div", {
                ref_key: "rootElement",
                ref: rootElement,
                class: "site-kit-monaco-editor-monaco"
              }, null, 512),
              _renderSlot(_ctx.$slots, "actions")
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createElementVNode("code", _hoisted_4, _toDisplayString(props.modelValue), 1),
              _createVNode(_Transition, { name: "site-kit-monaco-editor-fade" }, {
                default: _withCtx(() => [
                  (loading.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_5, _cache[0] || (_cache[0] = [
                        _createElementVNode("div", { class: "site-kit-monaco-editor-placeholder-loading-icon" }, [
                          _createElementVNode("div"),
                          _createElementVNode("div"),
                          _createElementVNode("div")
                        ], -1),
                        _createElementVNode("div", { class: "site-kit-monaco-editor-placeholder-loading-message" }, " Now loading... ", -1)
                      ])))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              })
            ]))
      ]),
      _: 3
    })
  ]))
}
}

})